import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

// Components
import CButton from "../../components/CButton";
import CSelect from "../../components/CSelect";
import CTextField from "../../components/CTextField";

// Config & Utils
import { supabase } from "../../config/supabaseClient";
import { getLocalData } from "../../constant/utils/commonUtils";

// Constants
import { 
  EAP_CONFIGURATION_FIELDS, 
  POPULATION_GROUPS, 
  EAP_REGIONS 
} from "../../constant/constants";

// Redux Actions
import { 
  insertEAPConfiguration, 
  updateEAPConfiguration, 
  fetchEAPConfiguration 
} from "../../slices/raceGroupSlice";

export default function SuperAdmin() {
  // ===== REDUX STATE =====
  const dispatch = useDispatch();
  const { 
    eapConfiguration = [], 
    eapLoading = false, 
    eapError = null 
  } = useSelector((state) => state.raceGroups || {});

  // ===== LOCAL STATE =====
  const [selectedRegion, setSelectedRegion] = useState(EAP_REGIONS[0]);
  const [populationGroups] = useState(POPULATION_GROUPS);
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeRegion, setActiveRegion] = useState(EAP_REGIONS[0]);
  const [regionsWithData, setRegionsWithData] = useState(new Set());

  // ===== CONSTANTS =====
  const companyId = JSON.parse(localStorage.getItem("sdfUser"))?.company_id;
  // ===== CALCULATED VALUES =====
  const totals = tableData.reduce(
    (acc, row) => {
      const mValue = Math.round((parseFloat(row.m) || 0) * 100);
      const fValue = Math.round((parseFloat(row.f) || 0) * 100);
      acc.m += mValue;
      acc.f += fValue;
      return acc;
    },
    { m: 0, f: 0 }
  );
  // Calculate total and convert back to decimal
  totals.total = (totals.m + totals.f) / 100;
  totals.m = totals.m / 100;
  totals.f = totals.f / 100;

  const isTotalExceeding100 = totals.total > 100;

  // ===== EFFECTS =====
  useEffect(() => {
    loadEAPData();
    checkRegionDataCompletion();
  }, [selectedRegion, populationGroups, dispatch]);

  // ===== DATA LOADING FUNCTIONS =====
  const checkRegionDataCompletion = async () => {
    const completedRegions = new Set();
    
    for (const region of EAP_REGIONS) {
      try {
        const isNational = region.isNational;
        const provinceName = isNational ? null : region.name;
        
        const result = await dispatch(fetchEAPConfiguration({ provinceName, isNational })).unwrap();
        
        if (result && result.length > 0) {
          // Check if all population groups have data and total is 100%
          const hasAllGroups = result.length === populationGroups.length;
          const totalPercentage = result.reduce((sum, item) => {
            return sum + (parseFloat(item.male_percentage) || 0) + (parseFloat(item.female_percentage) || 0);
          }, 0);
          
          if (hasAllGroups && Math.abs(totalPercentage - 100) < 0.01) {
            completedRegions.add(region.name);
          }
        }
      } catch (error) {
        console.error(`Error checking data for ${region.name}:`, error);
      }
    }
    
    setRegionsWithData(completedRegions);
  };

  const loadEAPData = async () => {
    
    if (!populationGroups.length) {
      return;
    }

    setIsLoading(true);
    
    try {
      const isNational = selectedRegion.isNational;
      const provinceName = isNational ? null : selectedRegion.name;
      
      const result = await dispatch(fetchEAPConfiguration({ provinceName, isNational })).unwrap();
      
      const rows = populationGroups.map((group) => {
        const found = result?.find((d) => {
          return d.race === group.key;
        });
        
        
        return {
          group: group.key,
          groupLabel: group.label,
          m: found ? found.male_percentage : "",
          f: found ? found.female_percentage : "",
          total: found
            ? (
                parseFloat(found.male_percentage || 0) +
                parseFloat(found.female_percentage || 0)
              ).toFixed(1)
            : "",
        };
      });
      
      setTableData(rows);
    } catch (error) {
      console.error("Error loading EAP data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // ===== INPUT HANDLING FUNCTIONS =====
  const handleInputChange = (rowIndex, field, value) => {
    if (field === 'm' || field === 'f') {
      // Remove any non-digit and non-decimal characters, but allow one decimal point
      const cleanValue = value.replace(/[^0-9.]/g, '');
      
      // Ensure only one decimal point
      const parts = cleanValue.split('.');
      if (parts.length > 2) {
        return; // Don't update if multiple decimal points
      }
      
      // Limit decimal places to 2
      if (parts.length === 2 && parts[1].length > 2) {
        return; // Don't update if more than 2 decimal places
      }
      
      // Allow values like 1234.56 (up to 4 digits before decimal, 2 after)
      // But still limit to reasonable percentage values (0-100)
      const numValue = parseFloat(cleanValue) || 0;
      if (numValue > 100) {
        return; // Don't update if value exceeds 100%
      }
      
      const newData = [...tableData];
      newData[rowIndex] = { ...newData[rowIndex], [field]: cleanValue };
      
      // Auto-calculate total for each row when M or F changes
      // Use integer arithmetic to avoid floating point precision issues
      const m = Math.round((parseFloat(newData[rowIndex].m) || 0) * 100);
      const f = Math.round((parseFloat(newData[rowIndex].f) || 0) * 100);
      const total = (m + f) / 100;
      newData[rowIndex].total = total.toFixed(2);
      
      setTableData(newData);
    }
  };

  // ===== REGION HANDLING FUNCTIONS =====
  const getItemClasses = (region) => {
    // Check if this region has 100% data
    const hasCompleteData = regionsWithData.has(region.name);
    
    if (selectedRegion.name === region.name) {
      return "text-blue-500 font-medium text-sm leading-sm p-4 border-b border-blue-100 cursor-pointer bg-blue-50";
    } else if (hasCompleteData) {
      return "text-black font-bold text-sm leading-sm p-4 border-b border-blue-100 cursor-pointer";
    } else {
      return "text-gray-300 font-medium text-sm leading-sm p-4 border-b border-blue-100 hover:bg-gray-50 cursor-pointer";
    }
  };

  const handleRegionClick = (region) => {
    setSelectedRegion(region);
  };

  // ===== SAVE/CANCEL FUNCTIONS =====
  const handleSave = async () => {
    // Validate total before saving - must be exactly 100%
    if (totals.total !== 100) {
      if (totals.total > 100) {
        toast.error(`Total percentage cannot exceed 100%. Current total: ${totals.total.toFixed(2)}%`);
      } else {
        toast.error(`Total percentage must be exactly 100%. Current total: ${totals.total.toFixed(2)}%`);
      }
      return;
    }

    setIsLoading(true);
    try {
      const isNational = selectedRegion.isNational;
      const provinceName = isNational ? null : selectedRegion.name;

      // Check if province already exists in database
      let existingData = null;
      if (isNational) {
        const { data } = await supabase
          .from("eap_configuration")
          .select("id")
          .is("province_name", null)
          .eq("is_national", true)
          .limit(1);
        existingData = data;
      } else {
        const { data } = await supabase
          .from("eap_configuration")
          .select("id")
          .eq("province_name", provinceName)
          .eq("is_national", false)
          .limit(1);
        existingData = data;
      }

      // Prepare data for each row
      const upsertData = tableData.map(row => ({
        is_national: isNational,
        province_name: provinceName,
        race: row.group,
        male_percentage: row.m === "" || row.m === null ? 0 : parseFloat(row.m),
        female_percentage: row.f === "" || row.f === null ? 0 : parseFloat(row.f),
      }));

      // Use appropriate function based on whether province exists
      if (existingData && existingData.length > 0) {
        await dispatch(updateEAPConfiguration(upsertData)).unwrap();
        toast.success("EAP configuration updated successfully!");
      } else {
        await dispatch(insertEAPConfiguration(upsertData)).unwrap();
        toast.success("EAP configuration saved successfully!");
      }
      
      // Refresh region data completion status after saving
      await checkRegionDataCompletion();
    } catch (error) {
      console.error("Error saving EAP configuration:", error);
      
      let errorMessage = "Failed to save EAP configuration";
      
      if (error && typeof error === 'object') {
        if (error.message) {
          errorMessage += `: ${error.message}`;
        } else if (error.error) {
          errorMessage += `: ${error.error}`;
        } else if (error.details) {
          errorMessage += `: ${error.details}`;
        } else if (error.toString) {
          errorMessage += `: ${error.toString()}`;
        }
      } else if (error && typeof error === 'string') {
        errorMessage += `: ${error}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = async () => {
    setIsLoading(true);
    
    try {
      const isNational = selectedRegion.isNational;
      const provinceName = isNational ? null : selectedRegion.name;
      
      const result = await dispatch(fetchEAPConfiguration({ provinceName, isNational })).unwrap();
      
      const rows = populationGroups.map((group) => {
        const found = result?.find((d) => d.race === group.key);
        return {
          group: group.key,
          groupLabel: group.label,
          m: found ? found.male_percentage : "",
          f: found ? found.female_percentage : "",
          total: found
            ? (
                parseFloat(found.male_percentage || 0) +
                parseFloat(found.female_percentage || 0)
              ).toFixed(1)
            : "",
        };
      });
      
      setTableData(rows);
      toast.info("Changes cancelled - data reset to original values");
    } catch (error) {
      console.error("Error resetting data:", error);
      toast.error("Failed to reset data. Please refresh the page.");
    } finally {
      setIsLoading(false);
    }
  };

  // ===== RENDER FUNCTIONS =====
  const renderRegionList = () => (
    <div className="w-[calc(30%-8px)] bg-white border border-blue-200 rounded-xl 2xl:h-[calc(100vh-164px)] h-[calc(100vh-144px)] overflow-hidden overflow-y-auto">
      <ul>
        {EAP_REGIONS.map((region) => (
          <li
            key={region.name}
            className={getItemClasses(region)}
            onClick={() => handleRegionClick(region)}
          >
            {region.name}
          </li>
        ))}
      </ul>
    </div>
  );

  const renderTable = () => (
    <div className="rounded-xl overflow-hidden border border-gray-200">
      <h2 className="text-center flex-1 text-base font-semibold p-3">
        EAP Target Decided ({selectedRegion.name})
      </h2>
      
      {isLoading ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-2 text-gray-600">Loading data...</span>
        </div>
      ) : (
        <table className="w-full border-collapse configuration-table">
          <thead>
            <tr className="bg-blue-50 font-semibold border-b border-t border-gray-200">
              <th className="font-semibold px-4 py-2 text-left bg-white border-l-0 text-sm leadimg-sm">
                Population Group
              </th>
              <th
                className="font-semibold px-4 py-2 text-center border-r-blue-50 text-sm leadimg-sm"
                colSpan={3}
              >
                %
              </th>
            </tr>
            <tr className="bg-blue-100 font-semibold border-b border-gray-200">
              <th className="font-semibold px-4 py-2 bg-white border-l-0"></th>
              <th className="font-semibold px-4 py-2 text-center text-sm leadimg-sm">
                M
              </th>
              <th className="font-semibold px-4 py-2 text-center text-sm leadimg-sm">
                F
              </th>
              <th className="font-semibold px-4 py-2 text-center border-r-blue-100 text-sm leadimg-sm">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((row, idx) => (
              <tr
                key={row.group}
                className="text-center border-b border-gray-200"
              >
                <td className="px-4 py-2 text-left bg-white border-l-0 text-sm leadimg-sm">
                  {row.groupLabel}
                </td>
                <td className="px-4 bg-gray-50 py-2 text-sm leadimg-sm">
                  <CTextField
                    value={row.m}
                    onChange={(e) => handleInputChange(idx, 'm', e.target.value)}
                    className="w-full text-center bg-transparent border-none outline-none text-sm"
                    maxLength={6}
                    inputMode="decimal"
                    pattern="[0-9.]*"
                    placeholder="0"
                  />
                </td>
                <td className="px-4 bg-gray-50 py-2 text-sm leadimg-sm">
                  <CTextField
                    value={row.f}
                    onChange={(e) => handleInputChange(idx, 'f', e.target.value)}
                    className="w-full text-center bg-transparent border-none outline-none text-sm"
                    maxLength={6}
                    inputMode="decimal"
                    pattern="[0-9.]*"
                    placeholder="0"
                  />
                </td>
                <td className="px-4 py-2 bg-gray-50 border-r-blue-100 text-sm leadimg-sm">
                  <span className="block w-full text-center">
                    {row.total}
                  </span>
                </td>
              </tr>
            ))}
            <tr className={`font-semibold ${isTotalExceeding100 ? 'bg-red-100' : 'bg-blue-100'}`}>
              <td className="px-4 py-2 text-sm leadimg-sm border-b-0 border-l-blue-100">
                Total
              </td>
              <td className="px-4 py-2 text-sm leadimg-sm border-b-0 text-center">
                {totals.m.toFixed(2)}%
              </td>
              <td className="px-4 py-2 text-sm leadimg-sm border-b-0 text-center">
                {totals.f.toFixed(2)}%
              </td>
              <td className={`px-4 py-2 text-sm leadimg-sm border-b-0 border-r-blue-100 text-center ${isTotalExceeding100 ? 'text-red-600 font-bold' : ''}`}>
                {totals.total.toFixed(2)}%
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );

  const renderWarning = () => (
    isTotalExceeding100 && (
      <div className="bg-red-50 border border-red-200 rounded p-3 mt-2">
        <p className="text-red-600 text-sm font-medium">
          Warning: Total percentage exceeds 100% ({totals.total.toFixed(2)}%). Please adjust values before saving.
        </p>
      </div>
    )
  );

  const renderActionButtons = () => (
    <div className="flex justify-end gap-2 mt-6 absolute bottom-4 right-4">
      <CButton 
        className="border border-blue-500 rounded px-6 py-2 bg-white text-blue-500 hover:bg-blue-500 hover:text-white" 
        onClick={handleCancel}
      >
        Cancel
      </CButton>
      <CButton 
        className="rounded px-6 py-2 bg-blue-500 text-white hover:bg-blue-700" 
        onClick={handleSave}
      >
        Save
      </CButton>
    </div>
  );

  // ===== MAIN RENDER =====
  return (
    <div className="relative">
      <h2 className="2xl:text-3xl 2xl:leading-3xl text-2xl leading-2xl text-blue-500 font-semibold 2xl:mb-4 md:mb-3 mb-4">
        Super Admin EAP Setup
      </h2>
      
      <div className="flex flex-wrap gap-4 h-full">
        {renderRegionList()}
        
        <div className="relative w-[calc(70%-8px)] bg-white border border-blue-200 rounded-xl 2xl:h-[calc(100vh-164px)] h-[calc(100vh-144px)] overflow-hidden p-4">
          {renderTable()}
          {renderWarning()}
          {renderActionButtons()}
        </div>
      </div>
    </div>
  );
}
